import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import "./Result.css";

// Helper function to map card name to image path
const getCardImage = (cardName) => {
  const cardMap = {
    'A': '/assets/img/aa2/A.png',
    '1': '/assets/img/aa2/A.png',
    '2': '/assets/img/aa2/2.png',
    '3': '/assets/img/aa2/3.png',
    '4': '/assets/img/aa2/4.png',
    '5': '/assets/img/aa2/5.png',
    '6': '/assets/img/aa2/6.png',
    '7': '/assets/img/aa2/7.png',
    '8': '/assets/img/aa2/8.png',
    '9': '/assets/img/aa2/9.png',
    '10': '/assets/img/aa2/10.png',
    'J': '/assets/img/aa2/J.png',
    'Q': '/assets/img/aa2/Q.png',
    'K': '/assets/img/aa2/K.png'
  };
  return cardMap[cardName] || '/assets/img/aa2/A.png';
};

// Helper function to extract card value from card code (e.g., "AHH" -> "A", "9CC" -> "9")
const extractCardValue = (cardCode) => {
  if (!cardCode) return null;
  const value = cardCode.replace(/(DD|SS|CC|HH)$/, '').trim();
  return value || null;
};

// Helper function to parse cards and categorize into Low (1-9) and High (10-K)
const parseCards = (cardString) => {
  if (!cardString) return { lowCards: [], highCards: [] };
  
  const cards = cardString.split(',');
  const lowCards = [];
  const highCards = [];
  
  cards.forEach((cardCode) => {
    const trimmedCode = cardCode.trim();
    const value = extractCardValue(trimmedCode);
    if (!value) return;
    
    // Use full card code for image path (e.g., "3HH.png", "7CC.png")
    const cardImage = `/assets/img/cards/${trimmedCode}.png`;
    
    // Low cards: A, 2-9
    if (value === 'A' || value === '1' || (parseInt(value) >= 2 && parseInt(value) <= 9)) {
      lowCards.push({ code: trimmedCode, value, image: cardImage });
    }
    // High cards: 10, J, Q, K
    else if (value === '10' || value === 'J' || value === 'Q' || value === 'K') {
      highCards.push({ code: trimmedCode, value, image: cardImage });
    }
  });
  
  return { lowCards, highCards };
};

// Helper function to map win value to result letter
const getWinLetter = (win) => {
  const winMap = {
    "0": "T", // Tie -> T
    "1": "L", // Low Card -> L
    "2": "H"  // High Card -> H
  };
  return winMap[win] || win;
};

const ResultCmeter = ({
  data = [],
  gameData = {},
  gameType = 'cmeter',
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(gameType);
        if (response?.success) {
          if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
            setLastResults(response.data.data.res);
          } else if (response?.data?.res && Array.isArray(response.data.res)) {
            setLastResults(response.data.res);
          }
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [gameType]);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);

    const mid = resultItem?.mid;

    if (mid) {
      try {
        const response = await getDetailResults(gameType, mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            setDetailResult(response.data.data.t1);
          } else if (response?.data?.t1) {
            setDetailResult(response.data.t1);
          }
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }

    setLoading(false);
    setShowModal(true);
  }, [gameType]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal || !detailResult) return null;

    const cardString = detailResult.card || "";
    const { lowCards, highCards } = parseCards(cardString);
    const winValue = detailResult.win;
    const winner = winValue === "1" ? "Low" : winValue === "2" ? "High" : "Tie";

    return {
      roundId: detailResult.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      lowCards,
      highCards,
      winner,
    };
  }, [selectedResult, detailResult, showModal, gameData]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults
    : data.map((res, idx) => ({ win: res, mid: null }));

  return (
    <>
      <div className="result-row lucky7 casino-last-results">
        {allResults.map((resultItem, index) => {
          const win = resultItem.win;
          const winLetter = getWinLetter(win);

          let resultClass = "";
          if (winLetter === "L") resultClass = "result-a";
          else if (winLetter === "H") resultClass = "result-b";
          else if (winLetter === "T") resultClass = "result-c";

          return (
            <div
              key={`${resultItem.mid || index}-${index}`}
              className={`result-circle ${resultClass}`}
              onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
              style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
            >
              {winLetter}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Card Meter Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <h6 className="text-right">
                  <b>Round Id :</b> {modalData.roundId}
                </h6>
              </div>
            </div>
            
            <div className="row row5 align-items-center cmeter">
              <div className="col-10">
                {/* Low Cards Section */}
                <div className="row align-items-center">
                  <div className="col-2">
                    <h6>Low Cards</h6>
                  </div>
                  <div className="col-10">
                    <div className="result-image">
                      {modalData.lowCards.map((card, index) => (
                        <img
                          key={index}
                          src={card.image}
                          alt={card.value}
                          className="mr-2"
                        />
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* High Cards Section */}
                <div className="row mt-3 align-items-center">
                  <div className="col-2">
                    <h6>High Cards</h6>
                  </div>
                  <div className="col-10">
                    <div className="result-image">
                      {modalData.highCards.map((card, index) => (
                        <img
                          key={index}
                          src={card.image}
                          alt={card.value}
                          className="mr-2"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-2 align-items-center text-right">
                <div className="result-image"></div>
              </div>
            </div>
            
            <div className="row board-result m-t-10">
              <div className="col-md-12">
                <div className="board-result-inner">
                  <div className="m-b-5 text-center">
                    <span className="greenbx">Winner: </span>
                    <span className="text-black">{modalData.winner}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultCmeter;

